import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ProgressModal from "../../components/modals/ProgressModal";

export function OrderDetailsViewModel(logout, orderId, addSnackbar) {
  const [carriers, setCarriers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [productsOptions, setProductsOptions] = useState([]);
  const [order, setOrder] = useState({ po_number: "" });
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [selectedItems, setSelectedItems] = useState([]);
  const [lastRow, setLastRow] = useState(null);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const getOrderDetails = async function ({ showLoading = true } = {}) {
    if (showLoading) {
      setLoading(true);
    }
    let callResult = await makeGetApiCall(`orders/${orderId}`);
    if (callResult.success) {
      setOrder(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    if (showLoading) {
      setLoading(false);
    }
  };

  const getCarriersForDropdown = async function () {
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarriers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getManufacturedProductsForDropdown = async function (search, page) {
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makeGetApiCall(
      `manufacturedProductsForDropdown`,
      payload,
    );
    if (callResult.success) {
      setProductsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getApiErrorDisplay = (data) => {
    if (!data) {
      return {
        title: "Error",
        subtitle: "We could not complete the action.",
      };
    }
    if (data.missing_plus?.length) {
      return {
        title: data.message || "Error",
        subtitle: `Missing PLUs in head office:\n${data.missing_plus.join("\n")}`,
      };
    }
    if (data.error) {
      return {
        title: data.message || "Error",
        subtitle: data.error,
      };
    }
    if (data.details?.length) {
      return {
        title: data.message || "Cannot unverify shipment",
        subtitle: data.details.join("\n"),
      };
    }
    return {
      title: "Error",
      subtitle:
        data.message || data.subtitle || "We could not complete the action.",
    };
  };

  const onMakeOrderApiCall = async function (payload) {
    var customer_id = null;
    if (payload.customer) {
      customer_id = payload.customer.value;
    }

    var carrier_id = null;
    if (payload.carrier) {
      carrier_id = payload.carrier.value;
    }

    var status = null;
    if (payload.status) {
      status = payload.status.value;
    }

    const newPayload = {
      ...payload,
      customer_id: customer_id,
      carrier_id: carrier_id,
      status: status,
      truck_question_1: payload.truck_question_1.value,
      truck_question_2: payload.truck_question_2.value,
      truck_question_3: payload.truck_question_3.value,
      truck_question_4: payload.truck_question_4.value,
    };
    showProgressModal("Updating your order now...");
    let callResult = await makePutApiCall(`orders/${orderId}`, newPayload);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Order updated",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getOrderDetails();
      return true;
    }
    if (callResult.errorStatus === 401) {
      logout();
      return false;
    }
    const { title, subtitle } = getApiErrorDisplay(callResult.data);
    showErrorModal(title, subtitle);
    return false;
  };

  const submitOrderUpdate = async function (payload) {
    showProgressModal("Updating order...");
    var allOrdersHavePrice = true;
    if (payload.status.value === 6) {
      payload.items.forEach((element) => {
        if (element.unit_cost <= 0) {
          allOrdersHavePrice = false;
        }
      });
    }

    if (allOrdersHavePrice) {
      const shouldCloseProgressModal = await onMakeOrderApiCall(payload);
      if (shouldCloseProgressModal !== false) {
        hideProgressModal();
      }
    } else {
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            buttonMessage="Continue"
            confirmAction={() => onMakeOrderApiCall(payload)}
            message={`Are you sure you want to continue with the order? Some products have no unit cost.`}
            hideReason={true}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const updateOrderApiCall = async function (payload) {
    const isHeadOfficeUnverify =
      payload.status.value === 13 &&
      Number(order.status) === 6 &&
      Number(order.customer_id) === 226;

    if (!isHeadOfficeUnverify) {
      return submitOrderUpdate(payload);
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Unverify shipment"
          confirmAction={(reason) => {
            submitOrderUpdate({
              ...payload,
              unverify_reason: reason,
            });
          }}
          message="This will remove pending, unused inventory from head office. Closed or used inventory cannot be reversed automatically."
          setOpen={setEmptyModelOpen}
          title="Unverify head-office shipment"
          requireReason={true}
        />
      ),
      open: true,
    });
  };

  const onUpdateOrderItem = async function (orderItem) {
    let callResult = await makePutApiCall(
      `orderItems/${orderItem.id}`,
      orderItem,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Item updated",
          SnackbarType.Success,
        ),
      );
      await getOrderDetails({ showLoading: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printOrderInvoice = async function () {
    let callResult = await makePostApiCall(`generateOrderInvoice/${orderId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printShippingSummary = async function () {
    let callResult = await makePostApiCall(
      `generateShippingSummaryReport/${orderId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printShippingDetails = async function () {
    let callResult = await makePostApiCall(
      `generateShippingDetailsReport/${orderId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `orderItems/${orderId}/${rowData.plu}/${rowData.lot_number}`,
    );
    if (callResult.success) {
      setRowDetails({ ...rowDetails, [rowData.unique_id]: callResult.data });
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `orderItems/pallets/${rowData.shipped_pallet_id}`,
    );
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCurrenciesForDropdown = async function (search, page) {
    let callResult = await makeGetApiCall(`currenciesForDropdown`);
    if (callResult.success) {
      setCurrencyOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  return {
    carriers,
    getCarriersForDropdown,
    customers,
    getCustomersForDropdown,
    getManufacturedProductsForDropdown,
    loading,
    showSimpleNotification,
    setEmptyModelOpen,
    setShowSimpleNotification,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateOrderApiCall,
    productsOptions,
    getOrderDetails,
    order,
    onUpdateOrderItem,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    currencyOptions,
    getCurrenciesForDropdown,
  };
}
